import {
  API_CURRENCY_ALL,
  API_EXCHANGE_RATES,
  LS_EMPLOYEE_ID,
  LS_I18NEXT_LANG,
  LS_THEME,
  LS_TOKEN,
  LS_CURRENCY_ID,
  LS_CURRENCY_NAME,
  PAGE_LOGIN,
  LS_CURRENCY_SYMBOL,
  LS_CURRENCY_CODE,
  LS_ORGANIZATION_ID,
} from "@/shared/constants";
import { Loader } from "@/shared/ui";
import { type ILoginData, setLoginData, setThemeState, setCurrencies, setExchangeRates, setOrganizations } from "@/store/reducers/LoginSlice";
import { useAppDispatch } from "@/store/storeHooks";
import { type UseQueryOptions } from "@tanstack/react-query";
import { useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useNetworkState } from 'react-use';
import { useApiQuery } from "@/shared/lib/hooks/api";


interface ApiResponse {
  code: number;
  data: ILoginData;
  message: string;
  statusCode: number;
  time: string;
}

interface CurrencyResponse {
  code: number;
  data: any[];
  message: string;
  statusCode: number;
  time: string;
}

interface ExchangeRatesResponse {
  code: number;
  data: any[];
  message: string;
  statusCode: number;
  time: string;
}

interface OrganizationResponse {
  code: number;
  data: any[];
  message: string;
  statusCode: number;
  time: string;
}

type ApiOptions = Omit<UseQueryOptions<ApiResponse, unknown>, 'queryKey'> & { toast?: boolean };
type CurrencyApiOptions = Omit<UseQueryOptions<CurrencyResponse, unknown>, 'queryKey'> & { toast?: boolean };
type ExchangeApiOptions = Omit<UseQueryOptions<ExchangeRatesResponse, unknown>, 'queryKey'> & { toast?: boolean };
type OrganizationApiOptions = Omit<UseQueryOptions<OrganizationResponse, unknown>, 'queryKey'> & { toast?: boolean };

export const AuthUser = () => {
  const dis = useAppDispatch();
  const hasToken = !!localStorage.getItem(LS_TOKEN);
  const navigate = useNavigate();
  const { online } = useNetworkState();

  const { isLoading, isFetching, data } = useApiQuery<ApiResponse>(
    "/employee/profile",
    {},
    {
      enabled: hasToken && online,
      onSuccess: (response: ApiResponse) => {
        try {
          dis(setLoginData(response.data));
          const theme = localStorage.getItem(LS_THEME);
          dis(setThemeState(theme || "light"));

          localStorage.setItem(LS_EMPLOYEE_ID, response.data._id);

          if (!localStorage.getItem(LS_I18NEXT_LANG)) {
            localStorage.setItem(LS_I18NEXT_LANG, "uz");
          }
        } catch (error) {
          console.error("Error in onSuccess handler:", error);
        }
      },
      onError() {
        localStorage.removeItem(LS_TOKEN);
        navigate(PAGE_LOGIN);
      },
    } as ApiOptions
  );

  // Fetch currency data
  useApiQuery<CurrencyResponse>(
    API_CURRENCY_ALL,
    {},
    {
      enabled: hasToken && online,
      onSuccess: (response: CurrencyResponse) => {
        try {
          // Save all currencies to Redux store
          if (response?.data && Array.isArray(response.data)) {
            dis(setCurrencies(response.data));
            
            // Find the default currency (is_main: true)
            const defaultCurrency = response?.data?.find((currency: any) => currency?.is_main);
            if (defaultCurrency) {
              localStorage.setItem(LS_CURRENCY_ID, defaultCurrency._id);
              localStorage.setItem(LS_CURRENCY_NAME, defaultCurrency.name);
              localStorage.setItem(LS_CURRENCY_SYMBOL, defaultCurrency.symbol);
              localStorage.setItem(LS_CURRENCY_CODE, defaultCurrency.system_code);
            }
          }
        } catch (error) {
          console.error("Error saving currency data:", error);
        }
      },
    } as CurrencyApiOptions
  );

  // Fetch exchange rates data
  useApiQuery<ExchangeRatesResponse>(
    API_EXCHANGE_RATES,
    {},
    {
      enabled: hasToken && online,
      onSuccess: (response: ExchangeRatesResponse) => {
        try {
          if (response?.data && Array.isArray(response.data)) {
            dis(setExchangeRates(response.data));
          }
        } catch (error) {
          console.error("Error saving exchange rates data:", error);
        }
      },
    } as ExchangeApiOptions
  );

  // Fetch organization data
  useApiQuery<OrganizationResponse>(
    "/organization/get-all",
    {},
    {
      enabled: hasToken && online,
      onSuccess: (response: OrganizationResponse) => {
        try {
          if (response?.data && Array.isArray(response.data)) {
            // Save all organizations to Redux store
            dis(setOrganizations(response.data));
            
            // Find organization with name "Asosiy"
            const mainOrganization = response.data.find(
              (org: any) => org?.name === "Asosiy"
            );
            if (mainOrganization) {
              localStorage.setItem(LS_ORGANIZATION_ID, mainOrganization._id);
            }
          }
        } catch (error) {
          console.error("Error saving organization data:", error);
        }
      },
    } as OrganizationApiOptions
  );

  useEffect(() => {
    if (data?.data) {
      dis(setLoginData(data.data));
      if (window.location.pathname === "/") {
        navigate("/cashbox/main");
      }
    }
  }, [data, dis, navigate]);

  if (isLoading || isFetching) {
    return <Loader className={"h-screen"} />;
  }
  return <Outlet />;
};